import React from "react";
import { useLocation, Link } from "react-router-dom";
import DesignPreviewShell from "@/components/DesignPreviewShell";
import { useDesignTheme } from "@/hooks/useDesignTheme";

/**
 * DesignPreviewStub — catches all /admin/design-preview/* routes that don't
 * have a dedicated preview page yet. Renders a themed placeholder inside the
 * sidebar shell so the user can walk the entire nav without hitting 404s.
 */

const META = {
  "/admin/design-preview/inbox": { icon: "📥", title: "Inbox & Comms", desc: "Twilio SMS/MMS threads, email drops, Kraaft photos, and Photo Command review lane." },
  "/admin/design-preview/team-chat": { icon: "💬", title: "Team Chat", desc: "@mentions, thread pins, day separators, and daily 6pm Ops Digest." },
  "/admin/design-preview/live-feed": { icon: "📡", title: "Live Feed", desc: "Real-time work order events, GPS pings, and photo drops from the field." },
  "/admin/design-preview/jobs": { icon: "🧾", title: "Work Orders", desc: "Kanban pipeline · Colored status flag counters strip up top · bulk actions." },
  "/admin/design-preview/schedule": { icon: "📅", title: "Weekly Schedule", desc: "Drag-and-drop rescheduling, month view, weather overlay per day." },
  "/admin/design-preview/lineup": { icon: "🚚", title: "Line-Up", desc: "Truck loadout, tomorrow's route order, sweeper pins, and inventory readouts." },
  "/admin/design-preview/sweeper": { icon: "🧹", title: "Sweeper Radar", desc: "Next-5 punch list with skip-forward, pinning, and truck assignment." },
  "/admin/design-preview/turns": { icon: "🔄", title: "Turns Board", desc: "Unit-turn pipeline: paint, clean, punch, ready-to-lease." },
  "/admin/design-preview/inspections": { icon: "🔍", title: "Inspections", desc: "Move-in, move-out, mid-lease, and property snapshot walks." },
  "/admin/design-preview/landlords": { icon: "👔", title: "Landlords", desc: "Owner rolodex, approval portals, and per-landlord P&L." },
  "/admin/design-preview/tenants": { icon: "🧍", title: "Tenants", desc: "Current + past occupants, tenant portal invites, and reported issues." },
  "/admin/design-preview/vendors": { icon: "🔌", title: "Vendors", desc: "Sub-contractor bench, insurance tracking, 1099 rollup, bid history." },
  "/admin/design-preview/portfolios": { icon: "🗂", title: "Portfolios", desc: "Major-portfolio hubs (ProCall, LCV, LaSalle) with cross-property views." },
  "/admin/design-preview/pmr-rebill": { icon: "💰", title: "PMR Rebill", desc: "Cost pass-through builder with OCR receipt-pull and per-line markup." },
  "/admin/design-preview/lcv-drop": { icon: "📄", title: "LCV Invoice Drop", desc: "Drop a vendor PDF → parsed line items → mapped WOs → exported invoice." },
  "/admin/design-preview/ready-to-bill": { icon: "✅", title: "Ready to Bill", desc: "Closed WOs waiting on billing action, grouped by landlord." },
  "/admin/design-preview/bookkeeper": { icon: "📚", title: "Bookkeeper", desc: "AR/AP snapshot, receipts inbox, payout reconciliation." },
  "/admin/design-preview/radar-move": { icon: "🚛", title: "Move Radar", desc: "Notice-to-vacate + move-in schedule fed by AppFolio." },
  "/admin/design-preview/radar-legal": { icon: "⚖️", title: "Legal Radar", desc: "Eviction filings, court dates, and lease-violation letters." },
  "/admin/design-preview/radar-appliance": { icon: "🧊", title: "Appliance IQ", desc: "Serialized appliance inventory + failure trends per property." },
  "/admin/design-preview/radar-request": { icon: "📢", title: "Request Radar", desc: "Public tenant request portal + auto-triaged WO drafts." },
  "/admin/design-preview/workers": { icon: "👷", title: "Workers", desc: "Employee roster, timesheets, pay rates, safety board." },
  "/admin/design-preview/subs": { icon: "🤝", title: "Sub-Contractors", desc: "Bench of subs, insurance expirations, 1099 threshold watch." },
  "/admin/design-preview/customers": { icon: "🧑", title: "Customers", desc: "One-off customers, quotes, and job history outside PM portfolios." },
  "/admin/design-preview/clickup": { icon: "🔗", title: "ClickUp Sync", desc: "Pull-missing scanner, auto-create toggle, custom_id extraction." },
  "/admin/design-preview/appfolio": { icon: "🏦", title: "AppFolio", desc: "Nightly WO + tenant + owner sync with drift detection." },
  "/admin/design-preview/kraaft": { icon: "📸", title: "Kraaft", desc: "Field photo threads mirrored into WO photo bundles." },
  "/admin/design-preview/twilio": { icon: "📱", title: "Twilio SMS/MMS", desc: "Inbound MMS review lane, outbound tenant blasts, opt-in mgmt." },
  "/admin/design-preview/integrations": { icon: "🧩", title: "All Integrations", desc: "Grid of every connected service with health, last-sync, and toggles." },
  "/admin/design-preview/ai-tools": { icon: "✨", title: "AI Tools", desc: "Radar AI · Ask Radar · AI Bid Drafter · Triage lane · Voice complete." },
  "/admin/design-preview/reports": { icon: "📊", title: "Reports", desc: "Weekly ops report, per-landlord P&L, PDF/CSV export center." },
  "/admin/design-preview/activity": { icon: "🕓", title: "Activity Log", desc: "Immutable audit trail — every mutation timestamped and attributed." },
  "/admin/design-preview/cleanup": { icon: "🧼", title: "Cleanup", desc: "Duplicate merges, auto-WO purge, orphan photo sweeper." },
  "/admin/design-preview/settings": { icon: "⚙️", title: "Settings", desc: "Workspace, users, integrations, notifications, killswitches." },
};

export default function DesignPreviewStub() {
  const location = useLocation();
  const { theme } = useDesignTheme();
  const m = META[location.pathname] || { icon: "🚧", title: "Preview stub", desc: "This route is part of the preview shell but doesn't have a dedicated mockup yet." };

  return (
    <DesignPreviewShell>
      <div className="font-sans">
        {/* Hero */}
        <div className={`${theme.gradient} pt-8 pb-24 px-4 sm:px-6`}>
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center gap-2 text-white/70 text-[10px] font-black uppercase tracking-widest">
              <Link to="/admin/design-preview" className="hover:text-white">← Dashboard</Link>
              <span>·</span>
              <span>Preview</span>
            </div>
            <div className="flex items-center gap-3 mt-2">
              <div className="text-4xl sm:text-5xl">{m.icon}</div>
              <div>
                <h1 className="text-white font-black text-2xl sm:text-4xl tracking-tight leading-tight">{m.title}</h1>
                <div className="text-white/80 text-xs sm:text-sm font-medium mt-1 max-w-2xl">{m.desc}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Floating card */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 -mt-16 pb-16">
          <div className="bg-white rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.15)] p-8 text-center">
            <div className="inline-flex items-center gap-2 h-8 px-3 rounded-full bg-[#00C4CC]/10 text-[#00807a] text-[10px] font-black uppercase tracking-widest">
              🚧 Design stub
            </div>
            <h2 className="mt-4 text-2xl font-black text-[#0F1F3A]">{m.title}</h2>
            <p className="mt-2 text-sm text-[#8B93A7] max-w-xl mx-auto">
              This screen is stubbed. Say <b className="text-[#0F1F3A]">"design {m.title.toLowerCase()}"</b> and
              I'll flesh it out in the preview — same sidebar, same look-and-feel.
            </p>
            <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3 max-w-2xl mx-auto">
              {["Header hero", "Filters strip", "Table/board", "Row action menu"].map(t => (
                <div key={t} className="p-3 rounded-xl border-2 border-dashed border-slate-200 text-[10px] font-black uppercase tracking-widest text-[#8B93A7]">
                  {t}
                </div>
              ))}
            </div>
            <div className="mt-6 flex items-center justify-center gap-2 flex-wrap">
              <Link to="/admin/design-preview" className="h-9 px-4 rounded-full bg-[#0F1F3A] text-white text-[10px] font-black uppercase tracking-widest hover:bg-[#1a2d4f] inline-flex items-center">
                ← Dashboard preview
              </Link>
              <Link to="/admin/design-preview/property" className="h-9 px-4 rounded-full bg-white border-2 border-[#0F1F3A] text-[#0F1F3A] text-[10px] font-black uppercase tracking-widest hover:bg-slate-50 inline-flex items-center">
                Property preview
              </Link>
            </div>
          </div>
        </div>
      </div>
    </DesignPreviewShell>
  );
}
